import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

const ICON_GRADIENTS = [
  'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)',
  'linear-gradient(135deg, #01b574 0%, #05a86688 100%)',
  'linear-gradient(135deg, #ee5d50 0%, #ff8c00 100%)',
  'linear-gradient(135deg, #ffd166 0%, #ff8c00 100%)',
];

function useCountUp(target: number, duration = 600): number {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const diff = target - start;
    if (diff === 0) return;
    const startTime = performance.now();
    let raf: number;
    const tick = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(start + diff * ease));
      if (t < 1) raf = requestAnimationFrame(tick);
      else prev.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return display;
}

export function StatCard({
  label,
  value,
  delta,
  icon,
  colorIndex = 0,
}: {
  label: string;
  value: string | number;
  delta?: { sign: '+' | '-' | '='; text: string };
  icon?: string;
  colorIndex?: number;
}) {
  const numeric = typeof value === 'number' ? value : parseInt(String(value), 10);
  const isNumeric = !isNaN(numeric);
  const animated = useCountUp(isNumeric ? numeric : 0);

  return (
    <div
      className="flex items-center justify-between rounded-2xl px-5 py-4 gap-3"
      style={{
        background: 'var(--glass-bg)',
        backdropFilter: 'blur(var(--glass-blur))',
        WebkitBackdropFilter: 'blur(var(--glass-blur))',
        border: '1px solid var(--glass-border)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
      }}
    >
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-text-secondary">
          {label}
        </div>
        <div className="mt-1.5 font-mono text-3xl font-bold tabular-nums text-text-primary leading-none">
          {isNumeric ? animated : value}
        </div>
        {delta && (
          <div
            className={clsx(
              'mt-1.5 text-xs font-mono',
              delta.sign === '+' && 'text-accent-green',
              delta.sign === '-' && 'text-accent-red',
              delta.sign === '=' && 'text-text-secondary',
            )}
          >
            {delta.sign}{delta.text}
          </div>
        )}
      </div>
      {icon && (
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-xl text-white shadow-lg"
          style={{ background: ICON_GRADIENTS[colorIndex % ICON_GRADIENTS.length] }}
        >
          {icon}
        </div>
      )}
    </div>
  );
}
